import { ChangeEvent, FocusEvent, useState, useRef, useContext, KeyboardEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Close as ClearText, DeleteOutline, History, Search as SearchIcon } from '@mui/icons-material';
import {
  IconButton,
  InputBase,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  styled,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { ctxPluto, SearchHistoryMode } from '@/contexts/PlutoContext';
import { isAppleDevice } from '@/utilities/sizeUtils';

import './Search.css';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[300],
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
  marginRight: 0,
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100% !important',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
  borderBottom: '2px solid transparent',
  '&:before': {
    content: "''",
    width: '100%',
    background: 'secondary.main',
    height: '2px',
    position: 'absolute',
    left: '0',
    bottom: '0',
    transform: 'scaleX(0)',
    transformOrigin: 'center',
    transition: 'transform 0.3s ease',
  },
  '&:focus-within:before': {
    transform: 'scaleX(1)',
  },
}));

const SearchHistoryContainer = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  position: 'absolute',
  top: '100%',
  left: '0',
  maxHeight: 'min(50dvh, 250px)',
  width: '100%',
  zIndex: '12',
  backgroundColor: theme.palette.grey[300],
  '& .MuiListItemSecondaryAction-root': {
    right: '0',
  },
  borderEndStartRadius: theme.shape.borderRadius,
  borderEndEndRadius: theme.shape.borderRadius,
}));

export function SearchComponent({
  searchText,
  setSearchText,
  placeholder,
  showHistory = true,
  refreshFunction,
  setHistoryMode,
}: {
  searchText: string | undefined;
  setSearchText: (searchText: string) => void;
  placeholder?: string | undefined;
  showHistory?: boolean;
  refreshFunction?: () => void;
  setHistoryMode?: SearchHistoryMode;
}) {
  const [searchTextLocal, setSearchTextLocal] = useState<string | undefined>(searchText);
  const plutoCtx = useContext(ctxPluto);
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const searchHistoryRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const searchHistory = plutoCtx?.searchHistory?.[setHistoryMode ?? 'target'] ?? [];

  function setSearchHistory(history: string[]) {
    const key = setHistoryMode ?? 'target';
    plutoCtx?.setSearchHistory((prev) => ({
      ...prev,
      [key]: history,
    }));
  }

  useEffect(() => {
    setSearchTextLocal(searchText);
    if (!showHistory) return;
    if (!searchText || !plutoCtx || searchHistory.includes(searchText)) return;
    const newHistory = [searchText, ...searchHistory];
    if (newHistory.length > 10) newHistory.length = 10;
    setSearchHistory(newHistory);
  }, [searchText]);

  function setSearchTextWrapper(event: ChangeEvent<HTMLInputElement>) {
    const searchText = event.target.value;
    setSearchTextLocal(searchText);
    if (searchText === '') {
      setSearchText(searchText);
      setIsFocused(false);
      inputRef.current?.blur();
    }
  }

  function handleClearTextButtonClick() {
    setSearchTextLocal('');
    setSearchText('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      setSearchText(event.currentTarget.value);
      setIsFocused(false);
      inputRef.current?.blur();
    }
  }

  function getPlaceholder(): string {
    if (placeholder) {
      return placeholder;
    }
    return t('filter.targetSearchPlaceholder');
  }

  function setClickedSearchHistory(text: string): void {
    setSearchText(text);
    setSearchTextLocal(text);
    setIsFocused(false);
    inputRef.current?.blur();
  }

  function deleteClickedSearchHistory(text: string): void {
    if (!plutoCtx) return;
    const newHistory = searchHistory.filter((t) => t !== text);
    setSearchHistory(newHistory);
  }

  function getRefreshButtonClass() {
    return 'search-refresh-button' + (searchTextLocal ? ' search-refresh-button-with-text' : '');
  }

  function getClearButtonClass() {
    return 'search-clear-button' + (searchTextLocal ? ' search-clear-button-with-text' : '');
  }

  const isHistoryOpen = showHistory && isFocused;
  const usePortalledHistory = isAppleDevice();

  const historyContent = (
    <SearchHistoryContainer
      ref={searchHistoryRef}
      className="search-history-container"
      data-qa-element-id="search_history_panel"
      sx={usePortalledHistory ? { position: 'static' } : undefined}
    >
      {searchHistory
        .filter((text) => text?.includes(searchTextLocal ? searchTextLocal : ''))
        .map((text) => (
          <ListItem
            data-qa-element-id="search_history_row"
            key={text}
            sx={{
              padding: 0,
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
            secondaryAction={
              <IconButton
                sx={{ right: '4px', padding: 0, ':hover': { backgroundColor: 'transparent' } }}
                data-qa-element-id="delete_button"
              >
                <DeleteOutline onClick={() => deleteClickedSearchHistory(text)} />
              </IconButton>
            }
          >
            <ListItemButton onClick={() => setClickedSearchHistory(text)} data-qa-element-id="search_term">
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <History />
              </ListItemIcon>
              <ListItemText primary={text} />
            </ListItemButton>
          </ListItem>
        ))}
    </SearchHistoryContainer>
  );

  if (!plutoCtx) return;

  return (
    <>
      <Search
        data-qa-element-id="search_panel"
        ref={searchRef}
        className="search-component"
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={(e: FocusEvent<HTMLDivElement>) => {
          const relatedTarget = e.relatedTarget as Node;
          if (!searchRef.current?.contains(relatedTarget) && !searchHistoryRef.current?.contains(relatedTarget)) {
            setIsFocused(false);
          } else if (searchRef.current?.contains(relatedTarget)) {
            if (!inputRef.current) return;
            inputRef.current?.focus();
          }
        }}
        style={isFocused ? { borderEndStartRadius: 0, borderEndEndRadius: 0 } : {}}
      >
        <SearchIconWrapper className="search-icon-wrapper">
          <SearchIcon data-qa-element-id="search_icon" />
        </SearchIconWrapper>
        <StyledInputBase
          inputProps={{ 'data-qa-element-id': 'search_input', enterKeyHint: 'search' }}
          type="search"
          inputRef={inputRef}
          value={searchTextLocal || ''}
          onChange={setSearchTextWrapper}
          placeholder={getPlaceholder()}
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiInputBase-input': {
              paddingLeft: '32px',
              paddingRight: searchTextLocal ? '30px' : '22px',
            },
          }}
        />
        {refreshFunction && <RefreshIcon className={getRefreshButtonClass()} onClick={() => refreshFunction()} />}
        <ClearText
          onClick={() => handleClearTextButtonClick()}
          className={getClearButtonClass()}
          data-qa-element-id="clear_search_icon"
        />
        {isHistoryOpen &&
          (usePortalledHistory ? (
            <Popper
              anchorEl={searchRef.current}
              open={true}
              placement="bottom-start"
              sx={(theme) => ({
                width: searchRef.current?.getBoundingClientRect().width,
                zIndex: theme.zIndex.modal + 1,
              })}
            >
              {historyContent}
            </Popper>
          ) : (
            <div className="search-history-container">{historyContent}</div>
          ))}
      </Search>
    </>
  );
}
